import React from 'react';
import {
  Box,
  LinearProgress,
  Typography,
  Chip,
  Collapse,
  IconButton,
  Paper,
  Button,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import SyncIcon from '@mui/icons-material/Sync';
import CloseIcon from '@mui/icons-material/Close';
import ReplayIcon from '@mui/icons-material/Replay';
import { OperationProgressProps } from './types';
import './OperationProgress.scss';

export const OperationProgress: React.FC<OperationProgressProps> = ({
  titles,
  useStore,
  onRetryFailed,
  retryCount,
}) => {
  const [errorsExpanded, setErrorsExpanded] = React.useState(false);
  const {
    isActive,
    totalOtcs,
    processed,
    success,
    failed,
    errors,
    status,
    reset,
  } = useStore();

  // Don't render if not active and not just completed
  if (!isActive && status !== 'complete') {
    return null;
  }

  const progressPercent = totalOtcs > 0 ? (processed / totalOtcs) * 100 : 0;
  const isComplete = status === 'complete';

  return (
    <Paper className="operationProgress" elevation={2}>
      <IconButton
        size="small"
        onClick={reset}
        aria-label="close"
        sx={{
          position: 'absolute',
          top: 4,
          right: 4,
          padding: '4px',
          color: '#666',
          '&:hover': { color: '#333' },
        }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
      <Box className="operationProgressHeader">
        {isComplete ? (
          <CheckCircleIcon className="operationProgressIcon operationProgressIconSuccess" />
        ) : (
          <SyncIcon className="operationProgressIcon operationProgressIconActive" />
        )}
        <Typography variant="subtitle1" className="operationProgressTitle">
          {isComplete ? titles.complete : titles.inProgress}
        </Typography>
      </Box>

      <Box className="operationProgressBar">
        <LinearProgress
          variant="determinate"
          value={progressPercent}
          color={isComplete ? 'success' : 'primary'}
        />
      </Box>

      <Box className="operationProgressStats">
        <Typography variant="body2" className="operationProgressCount">
          {processed} / {totalOtcs} OTCs processed ({progressPercent.toFixed(1)}
          %)
        </Typography>
        <Box className="operationProgressChips">
          <Chip
            icon={<CheckCircleIcon />}
            label={`${success} succeeded`}
            size="small"
            color="success"
            variant="outlined"
          />
          {failed > 0 && (
            <Chip
              icon={<ErrorIcon />}
              label={`${failed} failed`}
              size="small"
              color="error"
              variant="outlined"
            />
          )}
        </Box>
      </Box>

      {errors.length > 0 && (
        <Box className="operationProgressErrors">
          <Box
            className="operationProgressErrorsHeader"
            onClick={() => setErrorsExpanded(!errorsExpanded)}
          >
            <Box className="operationProgressErrorsHeaderContent">
              <ErrorIcon className="operationProgressErrorsHeaderIcon" />
              <Typography
                variant="body2"
                className="operationProgressErrorsHeaderText"
              >
                {failed} error{failed > 1 ? 's' : ''} occurred
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {onRetryFailed && retryCount != null && retryCount > 0 && (
                <Button
                  size="small"
                  color="warning"
                  variant="text"
                  startIcon={<ReplayIcon />}
                  onClick={(e) => { e.stopPropagation(); onRetryFailed(); }}
                >
                  Retry Failed ({retryCount})
                </Button>
              )}
              <IconButton
                size="small"
                className="operationProgressErrorsExpandButton"
              >
                {errorsExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Box>
          </Box>
          <Collapse in={errorsExpanded}>
            <Box className="operationProgressErrorsList">
              {errors.map((error, index) => (
                <Box key={index} className="operationProgressErrorItem">
                  <Typography
                    variant="caption"
                    className="operationProgressErrorItemText"
                    title={error}
                  >
                    {error}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Collapse>
        </Box>
      )}
    </Paper>
  );
};
